import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Student Theme context (dark vs light) with persistence.
 * Defaults to dark for students but respects an existing stored preference; if none stored,
 * will attempt to use system prefers-color-scheme as a gentle enhancement (still defaulting to dark if unavailable).
 * Safe for SSR (guards window/document usage) and memoizes the value object to avoid needless re-renders.
 */

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'aa_student_theme';

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true; // SSR fallback
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'dark') return true;
      if (stored === 'light') return false;
      // No stored preference; lightly respect system preference while biasing dark
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
        return false;
      }
    } catch { /* ignore */ }
    return true; // default dark
  });

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('student-dark-mode', isDark);
      document.body.classList.toggle('student-light-mode', !isDark);
    }
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light'); } catch { /* ignore */ }
    }
  }, [isDark]);

  const toggleTheme = () => setIsDark(prev => !prev);

  const value = useMemo<ThemeContextType>(() => ({ isDark, toggleTheme }), [isDark]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = (): ThemeContextType => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
};